import { useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import type {
  EntityType,
  RelatedEntitiesGroup,
  RelatedEntitiesResponse,
} from "../../types/types.MDM";
import { AccordionCard } from "../AccordionCard/AccordionCard";
import { TextSmRegular } from "../Typography/TypographyV2";
import { TablePageLayout } from "../TablePage/TablePageLayout";
import { TablePlaceholder } from "../../util/util";
import { useDebounce } from "../../util/hooks";
import { getRelatedEntitiesSearchPlaceholder } from "./utils";
import { PROFILE_DETAIL_CLASSES } from "../../layout/shared/ProfileDetailLayout";

const RELATED_ENTITIES_PAGE_SIZE = 20;

function useRelatedEntitiesGroupRows({
  groupAttributeId,
  tenantIdOrSlug,
  targetObjectType,
  targetObjectId,
  offset,
  perPage,
  searchQuery,
}: {
  groupAttributeId: string;
  tenantIdOrSlug: string;
  targetObjectType: EntityType | "product";
  targetObjectId: string;
  offset: number;
  perPage: number;
  searchQuery: string;
}) {
  const { data, isValidating } = useSWR<RelatedEntitiesResponse>(
    endpoints.v2_tenants_id_mdm_objects_object_type_object_id_related_entities(
      tenantIdOrSlug,
      targetObjectType,
      targetObjectId
    ) +
      `?group_attribute_id=${groupAttributeId}&offset=${offset}&limit=${perPage}&q=${encodeURIComponent(
        searchQuery
      )}`,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    currentGroup: data?.groups?.[0],
    isLoading: isValidating,
  };
}

export const buildRelatedEntitiesColumns = ({
  group,
  rows,
  t,
  getSourceHref,
}: {
  group: RelatedEntitiesGroup;
  rows: RelatedEntitiesGroup["source_profiles"];
  t: TFunction;
  getSourceHref: (profile: {
    id: string;
    object_type: EntityType | "product";
  }) => string;
}) => {
  const previewColumns = (group.source_preview_attributes ?? []).map(
    (previewAttribute) => ({
      header: () =>
        previewAttribute.display_name || previewAttribute.name || t("Field"),
      id: `preview_${previewAttribute.id}`,
      accessorFn: (source: typeof rows[number]) => {
        const previewList = source.preview_attributes ?? [];
        const label =
          previewAttribute.display_name || previewAttribute.name || "";
        return previewList.find((p) => p.key === label)?.value ?? "-";
      },
      cell: ({
        row: { original },
        getValue,
      }: {
        row: { original: typeof rows[number] };
        getValue: () => unknown;
      }) => {
        const href = getSourceHref({
          id: original.id,
          object_type: original.object_type,
        });
        return getValue() ? (
          <a href={href}>{String(getValue() ?? "-")}</a>
        ) : (
          "-"
        );
      },
    })
  );

  return previewColumns.length > 0 ? [...previewColumns] : [];
};

function RelatedEntitiesGroupTable({
  group,
  tenantIdOrSlug,
  targetObjectType,
  targetObjectId,
  getSourceHref,
  searchValue,
  offset,
  perPage,
  onSearchChange,
  onSearchClear,
  onOffsetChange,
  onPerPageChange,
}: {
  group: RelatedEntitiesResponse["groups"][number];
  tenantIdOrSlug: string;
  targetObjectType: EntityType | "product";
  targetObjectId: string;
  getSourceHref: (profile: {
    id: string;
    object_type: EntityType | "product";
  }) => string;
  searchValue: string;
  offset: number;
  perPage: number;
  onSearchChange: (nextValue: string) => void;
  onSearchClear: () => void;
  onOffsetChange: (nextOffset: number) => void;
  onPerPageChange: (nextPerPage: number) => void;
}) {
  const { t } = useTranslation();
  const searchQuery = searchValue.trim();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const { currentGroup, isLoading: isRowsLoading } =
    useRelatedEntitiesGroupRows({
      groupAttributeId: group.attribute_id,
      tenantIdOrSlug,
      targetObjectType,
      targetObjectId,
      offset,
      perPage,
      searchQuery: debouncedSearchQuery,
    });
  const rows =
    currentGroup?.source_profiles ??
    (offset === 0 && searchQuery.length === 0
      ? group.source_profiles ?? []
      : []);
  const totalRows =
    currentGroup?.total_count ??
    (offset === 0 && searchQuery.length === 0
      ? group.total_count ?? group.source_profiles?.length ?? 0
      : 0);
  const pageCount = Math.ceil(totalRows / perPage);
  const pageIndex = Math.floor(offset / perPage) + 1;
  const searchPlaceholder = getRelatedEntitiesSearchPlaceholder(group, t);
  const columns = buildRelatedEntitiesColumns({
    group,
    rows,
    t,
    getSourceHref,
  });

  useEffect(() => {
    if (offset > 0 && totalRows > 0 && offset >= totalRows) {
      onOffsetChange(0);
    }
  }, [offset, totalRows, onOffsetChange]);

  return (
    <div
      className={PROFILE_DETAIL_CLASSES.relatedEntities}
      key={group.attribute_id}
    >
      <AccordionCard
        title={group.target_side_display_name}
        expanded={true}
        actionChildren={() => <></>}
        hideHeaderBorder={true}
      >
        <TablePageLayout
          title={""}
          showTitle={true}
          searchQuery={searchValue}
          searchPlaceholder={searchPlaceholder}
          onSearchChange={(e) => onSearchChange(e.target.value)}
          onSearchClear={onSearchClear}
          toolbarActions={<></>}
          totalCount={totalRows}
          perPageItems={[10, 20, 50]}
          perPage={perPage}
          onPerPageChange={(nextPerPage) =>
            onPerPageChange(Number(nextPerPage))
          }
          columns={columns}
          data={rows}
          isLoading={isRowsLoading}
          error={undefined}
          rowClick={undefined}
          Placeholder={<TablePlaceholder message={t("No results found")} />}
          pagination={
            pageCount > 1
              ? {
                  pageCount,
                  pageIndex,
                  perPage,
                }
              : undefined
          }
          offset={offset}
          onPageChange={onOffsetChange}
          lastChildleftAlign
        />
      </AccordionCard>
    </div>
  );
}

/** Wait for the related-entities API before building tabs when object reference is enabled. */
export function getIsRelatedEntitiesReady({
  objectId,
  objectReferenceEnabled,
  relatedEntities,
  relatedEntitiesError,
}: {
  objectId?: string;
  objectReferenceEnabled: boolean;
  relatedEntities?: RelatedEntitiesResponse;
  relatedEntitiesError?: unknown;
}): boolean {
  if (!objectReferenceEnabled || !objectId) {
    return true;
  }
  if (relatedEntitiesError) {
    return true;
  }
  return relatedEntities !== undefined;
}

export function getShouldShowRelatedEntitiesTab({
  objectReferenceEnabled,
  relatedEntities,
}: {
  objectReferenceEnabled: boolean;
  relatedEntities?: RelatedEntitiesResponse;
}): boolean {
  return Boolean(
    objectReferenceEnabled && relatedEntities?.has_related_entities
  );
}

export function useRelatedEntities(
  tenantIdOrSlug: string,
  targetObjectType: EntityType | "product",
  targetObjectId?: string,
  objectReferenceEnabled = false
) {
  const endpoint =
    objectReferenceEnabled && targetObjectId != null
      ? endpoints.v2_tenants_id_mdm_objects_object_type_object_id_related_entities(
          tenantIdOrSlug,
          targetObjectType,
          targetObjectId
        )
      : null;

  return useSWR<RelatedEntitiesResponse>(endpoint, {
    revalidateOnFocus: false,
  });
}

export function RelatedEntitiesTabContent({
  tenantIdOrSlug,
  targetObjectType,
  targetObjectId,
  relatedEntities,
  getSourceHref,
}: {
  tenantIdOrSlug: string;
  targetObjectType: EntityType | "product";
  targetObjectId: string;
  relatedEntities: RelatedEntitiesResponse;
  getSourceHref: (profile: {
    id: string;
    object_type: EntityType | "product";
  }) => string;
}): JSX.Element {
  const { t } = useTranslation();
  const [offsetByGroup, setOffsetByGroup] = useState<Record<string, number>>(
    {}
  );
  const [searchByGroup, setSearchByGroup] = useState<Record<string, string>>(
    {}
  );
  const [perPageByGroup, setPerPageByGroup] = useState<Record<string, number>>(
    {}
  );

  return (
    <>
      {relatedEntities.groups.map((group) => {
        const groupId = group.attribute_id;
        return (
          <RelatedEntitiesGroupTable
            key={groupId}
            group={group}
            tenantIdOrSlug={tenantIdOrSlug}
            targetObjectType={targetObjectType}
            targetObjectId={targetObjectId}
            getSourceHref={getSourceHref}
            searchValue={searchByGroup[groupId] ?? ""}
            offset={offsetByGroup[groupId] ?? 0}
            perPage={perPageByGroup[groupId] ?? RELATED_ENTITIES_PAGE_SIZE}
            onSearchChange={(nextValue) => {
              setSearchByGroup((prev) => ({
                ...prev,
                [groupId]: nextValue,
              }));
              setOffsetByGroup((prev) => ({
                ...prev,
                [groupId]: 0,
              }));
            }}
            onSearchClear={() => {
              setSearchByGroup((prev) => ({
                ...prev,
                [groupId]: "",
              }));
              setOffsetByGroup((prev) => ({
                ...prev,
                [groupId]: 0,
              }));
            }}
            onOffsetChange={(nextOffset) =>
              setOffsetByGroup((prev) => ({
                ...prev,
                [groupId]: nextOffset,
              }))
            }
            onPerPageChange={(nextPerPage) => {
              setPerPageByGroup((prev) => ({
                ...prev,
                [groupId]: nextPerPage,
              }));
              setOffsetByGroup((prev) => ({
                ...prev,
                [groupId]: 0,
              }));
            }}
          />
        );
      })}
      {relatedEntities.groups.length === 0 && (
        <TextSmRegular>{t("No related entities found.")}</TextSmRegular>
      )}
    </>
  );
}
